import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { refreshToken } from '@/Config/auth/authStore';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Spinner } from '@/Domain/components/Shared/Spinner';

type TransferOwnershipDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** User id of the member receiving ownership. */
  userId: string;
  email: string;
};

export default function TransferOwnershipDialog({
  open,
  onOpenChange,
  userId,
  email,
}: TransferOwnershipDialogProps): JSX.Element {
  const { t } = useTranslation(['users', 'common']);
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState('');

  // Confirm against the recipient email, not the agent name (most agents have no display name).
  const isConfirmed = confirmation.trim().toLowerCase() === email.toLowerCase();

  useEffect(() => {
    setConfirmation('');
  }, [open]);

  const { mutate, isPending } = useMutation<unknown[], AxiosError<ErrorResponse>, string>({
    mutationKey: [QueryKey.scenarioUsers.transferOwnership],
    onSuccess: async (members) => {
      queryClient.setQueryData(['users'], members);
      // The JWT roles claim is the sole API authority, so re-mint it to drop the owner claim now.
      await refreshToken();
      // Drives permissions.delete_ai_agent, which the actor just lost.
      await queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
      onOpenChange(false);
      toast.success(t('transfer_ownership.success', { email }));
    },
    onError: (error) => {
      // A transport failure can lose the response of a transfer that committed, and the
      // retry this toast invites would 403; refetch so the row reflects the real outcome.
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.error(
        error.response?.data?.errors?.root?.[0]?.message || t('transfer_ownership.error'),
      );
    },
  });

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!isPending) onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <CircleAlert className="size-4 bg-warning-muted rounded-lg text-warning p-1.5 box-content" />
            {t('transfer_ownership.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                <Trans
                  i18nKey="transfer_ownership.description"
                  t={t}
                  values={{ email }}
                  components={{ bold: <b /> }}
                />
              </p>
              <p>{t('transfer_ownership.consequences')}</p>
              <div className="space-y-1.5">
                <Label htmlFor="transfer-ownership-confirmation">
                  <Trans
                    i18nKey="transfer_ownership.confirm_label"
                    t={t}
                    values={{ email }}
                    components={{ bold: <b /> }}
                  />
                </Label>
                <Input
                  id="transfer-ownership-confirmation"
                  value={confirmation}
                  autoComplete="off"
                  disabled={isPending}
                  onChange={(event) => setConfirmation(event.target.value)}
                  data-testid="transfer-ownership-confirmation-input"
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild disabled={isPending}>
            <Button variant="secondary" disabled={isPending}>
              {t('cancel', { ns: 'common' })}
            </Button>
          </AlertDialogCancel>
          <Button
            variant="dark"
            disabled={!isConfirmed || isPending}
            onClick={() => mutate(userId)}
            className="relative"
            data-testid="transfer-ownership-confirm-button"
          >
            <span className={isPending ? 'invisible' : ''}>{t('transfer_ownership.action')}</span>
            {isPending && (
              <span className="absolute inset-0 flex items-center justify-center">
                <Spinner className="h-4 w-4 text-white" />
              </span>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
